import type { MeasurementImportAttempt } from "../utils/measurementImportExecution";
/**
 * Centralized PostHog analytics service.
 *
 * All custom event tracking goes through this module so that event names,
 * property shapes, and capture calls are defined in one place.
 */

import posthog from "posthog-js";
import type { QuickStartFlow } from "../utils/quickStartFlow.ts";
import type { GenerateMode } from "./backendAiService";

// ---------------------------------------------------------------------------
// AI Generation / Analysis events
// ---------------------------------------------------------------------------

interface AiRequestProperties {
  mode: GenerateMode | string;
  provider: string;
  model?: string;
  success: boolean;
  durationMs: number;
  errorMessage?: string;
  /** Project the request was initiated from (from generation context). */
  projectId?: string;
  /** Product the request was initiated from (from generation context). */
  productId?: string;
  /** User-facing generation category (e.g. "Re-Coloring", "Standard"). */
  imageType?: string;
  /**
   * Storage paths of all source images passed to the backend. Lets us trace
   * which source image a generation was derived from and whether the same
   * image is being iteratively edited.
   */
  sourceImagePaths?: string[];
  /** Extra context (e.g. upscaleFactor, numResults) */
  [key: string]: unknown;
}

export function trackAiRequest(props: AiRequestProperties): void {
  posthog.capture("ai_request", props);
}

// ---------------------------------------------------------------------------
// Project lifecycle
// ---------------------------------------------------------------------------

export function trackProjectCreated(projectId: string): void {
  posthog.capture("project_created", { projectId });
}

export function trackProjectDeleted(projectId: string): void {
  posthog.capture("project_deleted", { projectId });
}

// ---------------------------------------------------------------------------
// Product lifecycle
// ---------------------------------------------------------------------------

export function trackProductAdded(projectId: string, productId: string): void {
  posthog.capture("product_added", { projectId, productId });
}

export function trackProductDeleted(
  projectId: string,
  productId: string
): void {
  posthog.capture("product_deleted", { projectId, productId });
}

// ---------------------------------------------------------------------------
// Image actions
// ---------------------------------------------------------------------------

export function trackImageUploaded(projectId: string, productId: string): void {
  posthog.capture("image_uploaded", { projectId, productId });
}

/** Each attempt has one event; changedCount contains only new durable writes.
 * Use importId to group retries. The new event keeps the legacy width/height
 * import's historical metrics separate from flexible product measurements. */
export function trackProductMeasurementsImportAttempt(
  props: MeasurementImportAttempt & { projectId: string }
): void {
  posthog.capture("product_measurements_import_attempt", props);
}

interface ImageDownloadedProperties {
  format: "png" | "jpeg" | "mp4";
  /** Firebase Storage path — canonical identifier, stable across sessions. */
  storagePath?: string;
  /** GeneratedContent DB id (AI-generated images only). */
  imageId?: string;
  /** Whether the download was an original upload or an AI-generated result. */
  source?: "source" | "generated";
  projectId?: string;
  productId?: string;
  /** Whether the opt-in "AI-generated" badge was stamped onto the download. */
  aiWatermark?: boolean;
  /** Kind of badge stamped: designed pill or uploaded label file (SIS-481). */
  aiBadgeKind?: "styled" | "image";
  /** Which pipeline delivered the file: the backend `exportImage` endpoint
   *  (fresh C2PA signature) or the existing client-side pipeline (SIS-482). */
  deliveryPath?: "backend_export" | "client";
}

export function trackImageDownloaded(props: ImageDownloadedProperties): void {
  posthog.capture("image_downloaded", props);
}

/**
 * A generated image was renamed from the viewer header. `surface` says which
 * gallery the rename was made from, since the two reach different code paths.
 * The name itself is never sent — it is user content.
 */
export function trackImageRenamed(props: {
  imageId: string;
  surface: "project_library" | "global_library";
  projectId?: string;
  productId?: string;
}): void {
  posthog.capture("image_renamed", props);
}

// ---------------------------------------------------------------------------
// Brand templates
// ---------------------------------------------------------------------------

export function trackBrandTemplateCreated(brandTemplateId: string): void {
  posthog.capture("brand_template_created", { brandTemplateId });
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export function trackWorkflowCreated(
  projectId: string,
  workflowId: string
): void {
  posthog.capture("workflow_created", { projectId, workflowId });
}

export function trackWorkflowExecuted(
  projectId: string,
  workflowId: string,
  productCount: number
): void {
  posthog.capture("workflow_executed", {
    projectId,
    workflowId,
    productCount,
  });
}

// ---------------------------------------------------------------------------
// Signup
// ---------------------------------------------------------------------------

export const AUTH_FLOW_VERSION = "sis_544";

export type AuthMode = "login" | "signup";
export type AuthMethod = "magic_link" | "password";

interface AuthModeViewedProperties {
  mode: AuthMode;
  source: string;
  hasPrefilledEmail: boolean;
}

type MagicLinkProperties = AuthModeViewedProperties;

interface AuthFormSubmittedProperties extends AuthModeViewedProperties {
  method: AuthMethod;
}

interface AuthFailedProperties {
  mode: AuthMode | "unknown";
  method: AuthMethod;
  source: string;
  hasPrefilledEmail: boolean;
  reason: string;
  stage: "submit" | "resend" | "callback";
}

interface AuthCompletedProperties {
  mode: AuthMode;
  requestedMode: AuthMode | "unknown";
  method: AuthMethod;
  source: string;
  hasPrefilledEmail: boolean;
  emailReentered: boolean;
}

function withAuthFlowVersion<T extends object>(props: T) {
  return { ...props, flowVersion: AUTH_FLOW_VERSION };
}

export function trackAuthModeViewed(props: AuthModeViewedProperties): void {
  posthog.capture("auth_mode_viewed", withAuthFlowVersion(props));
}

export function trackAuthFormSubmitted(
  props: AuthFormSubmittedProperties
): void {
  posthog.capture("auth_form_submitted", withAuthFlowVersion(props));
}

export function trackMagicLinkSent(props: MagicLinkProperties): void {
  posthog.capture("magic_link_sent", withAuthFlowVersion(props));
}

export function trackMagicLinkResent(props: MagicLinkProperties): void {
  posthog.capture("magic_link_resent", withAuthFlowVersion(props));
}

export function trackSignupEmailChanged(source: string): void {
  posthog.capture("signup_email_changed", withAuthFlowVersion({ source }));
}

export function trackAuthFailed(props: AuthFailedProperties): void {
  posthog.capture("auth_failed", withAuthFlowVersion(props));
}

export function trackAuthCompleted(
  userId: string,
  props: AuthCompletedProperties
): void {
  posthog.identify(userId);
  posthog.capture("auth_completed", withAuthFlowVersion(props));
}

export function trackSignupStarted(source: string): void {
  posthog.capture("signup_started", withAuthFlowVersion({ source }));
}

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

export function trackOnboardingCompleted(): void {
  posthog.capture("onboarding_completed");
}

// ---------------------------------------------------------------------------
// Quick-start tour
// ---------------------------------------------------------------------------

export type QuickstartStage =
  | "open_project"
  | "upload"
  | "configure"
  | "generate"
  | "generating"
  | "view_result"
  // Video flow (SIS-566): pick a preset, then the "How it works" card takes over.
  | "preset"
  | "how_it_works";

export function trackQuickstartStarted(flow: QuickStartFlow): void {
  posthog.capture("quickstart_started", { flow });
}

/**
 * `flow` is the guided tour the stage belongs to (SIS-566). `mode` is the
 * creation catalog entry id the stage was reached in (e.g. `cutout`), or
 * `workflow`, so the images funnel can be read per goal (SIS-565). It is
 * absent on `open_project` and `upload`, which precede the goal choice.
 */
export function trackQuickstartStageReached(
  stage: QuickstartStage,
  flow: QuickStartFlow,
  mode?: string
): void {
  posthog.capture(
    "quickstart_stage_reached",
    mode ? { stage, flow, mode } : { stage, flow }
  );
}

export function trackQuickstartSkipped(
  stage: QuickstartStage | null,
  flow: QuickStartFlow
): void {
  posthog.capture("quickstart_skipped", { stage, flow });
}

export function trackQuickstartCompleted(flow: QuickStartFlow): void {
  posthog.capture("quickstart_completed", { flow });
}

// ---------------------------------------------------------------------------
// Creation catalog (SIS-558)
// ---------------------------------------------------------------------------

/** Where a creation goal card was chosen. */
export type CreationGoalSurface =
  "studio_panel" | "empty_project" | "onboarding";

export function trackCreationGoalSelected(props: {
  /** Catalog entry id, e.g. `cutout`. */
  goalId: string;
  surface: CreationGoalSurface;
  projectId?: string;
}): void {
  posthog.capture("creation_goal_selected", props);
}

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

export function trackSubscriptionPageViewed(): void {
  posthog.capture("subscription_page_viewed");
}

// ---------------------------------------------------------------------------
// ShopVibes integration
// ---------------------------------------------------------------------------

/** Fired on the way back from the Shopvibes OAuth callback (SIS-580). */
export function trackShopVibesAccountConnected(): void {
  posthog.capture("shopvibes_account_connected");
}

/** Fired when the account is disconnected in Settings -> Integrations. */
export function trackShopVibesAccountDisconnected(): void {
  posthog.capture("shopvibes_account_disconnected");
}

// ---------------------------------------------------------------------------
// Referral hub (SIS-307)
// ---------------------------------------------------------------------------

export function trackReferralLinkGenerated(referralCode: string): void {
  posthog.capture("referral_link_generated", { referralCode });
}

export function trackReferralLinkCopied(referralCode: string): void {
  posthog.capture("referral_link_copied", { referralCode });
}

/**
 * Captured when a user clicks the "Open" button on a review portal task in
 * the gift hub. The portal site can't tell us whether the user actually
 * submitted a review, but click-through rate is the leading indicator we
 * have for review-task engagement.
 */
export function trackReviewPortalOpened(
  platform: "OMR" | "Capterra" | "G2"
): void {
  posthog.capture("review_portal_opened", { platform });
}

/**
 * Frontend-side counterpart to the backend `referral_signup_recorded` event.
 * Captured on the referee's session right after recordReferralSignup confirms
 * the attribution landed.
 */
export function trackReferralSignupAttributed(referralCode: string): void {
  posthog.capture("referral_signup_attributed", { referralCode });
}

// ---------------------------------------------------------------------------
// Marketplace export (SIS-50)
// ---------------------------------------------------------------------------

interface MarketplaceExportOpenedProperties {
  /** GeneratedContent id the export was launched from. */
  generationId: string;
  sourceWidth?: number;
  sourceHeight?: number;
  projectId?: string;
  productId?: string;
}

export function trackMarketplaceExportOpened(
  props: MarketplaceExportOpenedProperties
): void {
  posthog.capture("marketplace_export_opened", props);
}

interface MarketplaceExportDownloadedProperties {
  generationId: string;
  /** Preset the image was exported for (e.g. "amazon_main"). */
  presetId: string;
  fitMode: "pad" | "cover";
  fillColor: string;
  format: string;
  sourceWidth?: number;
  sourceHeight?: number;
  outputWidth: number;
  outputHeight: number;
  outputBytes: number;
  /** Whether the exported variant was also saved to the gallery. */
  saved?: boolean;
  /** Whether the opt-in "AI-generated" badge was stamped onto the export. */
  aiWatermark?: boolean;
  /** Kind of badge stamped: designed pill or uploaded label file (SIS-481). */
  aiBadgeKind?: "styled" | "image";
  /**
   * Which pipeline delivered the file (SIS-492), mirroring
   * `image_downloaded.deliveryPath`. `backend_export` means the bytes came
   * from `exportImage` and carry C2PA Content Credentials; `client` means the
   * canvas blob was delivered instead — either deliberately (no stored
   * source, or a format the endpoint cannot encode) or because the backend
   * call failed and the silent fallback took over. This is the only
   * aggregate view of that fallback rate.
   */
  deliveryPath?: "backend_export" | "client";
  projectId?: string;
  productId?: string;
}

export function trackMarketplaceExportDownloaded(
  props: MarketplaceExportDownloadedProperties
): void {
  posthog.capture("marketplace_export_downloaded", props);
}

interface MarketplaceExportSavedProperties {
  generationId: string;
  /** Preset the image was exported for (e.g. "amazon_main"). */
  presetId: string;
  fitMode: "pad" | "cover";
  fillColor: string;
  format: string;
  sourceWidth?: number;
  sourceHeight?: number;
  outputWidth: number;
  outputHeight: number;
  outputBytes: number;
  /** Whether the opt-in "AI-generated" badge was stamped onto the export. */
  aiWatermark?: boolean;
  /** Kind of badge stamped: designed pill or uploaded label file (SIS-481). */
  aiBadgeKind?: "styled" | "image";
  /**
   * Whether the saved object carries C2PA Content Credentials (SIS-492).
   * False means the signing detour failed and the silent fallback stored
   * the variant unsigned — this is how that rate stays observable in
   * PostHog rather than only in Sentry. Undefined for a download-only
   * export, which stores nothing.
   */
  c2paSigned?: boolean;
  projectId?: string;
  productId?: string;
}

/** Fired when a variant is saved to the gallery WITHOUT a file download. */
export function trackMarketplaceExportSaved(
  props: MarketplaceExportSavedProperties
): void {
  posthog.capture("marketplace_export_saved", props);
}

interface MarketplaceExportWarningShownProperties {
  generationId: string;
  warningType: "upscaling" | "filesize_cap";
  presetId?: string;
}

export function trackMarketplaceExportWarningShown(
  props: MarketplaceExportWarningShownProperties
): void {
  posthog.capture("marketplace_export_warning_shown", props);
}

// ---------------------------------------------------------------------------
// Set image (SIS-465)
// ---------------------------------------------------------------------------

interface SetImageCreatedProperties {
  projectId: string;
  productId: string;
  /** Number of source images composed into the set. */
  imageCount: number;
  /** Layout preset id (e.g. "grid", "row"). */
  layout: string;
  /** Output resolution label (e.g. "2K", "3K"). */
  resolution?: string;
  gutterPx?: number;
  backgroundColor?: string;
  outputWidth?: number;
  outputHeight?: number;
  outputBytes?: number;
  /** True when a company logo overlay was baked into the set image. */
  logo?: boolean;
  /** True when re-editing an existing set image (free, no credit charge). */
  reEdit?: boolean;
  /**
   * Whether the saved object carries C2PA Content Credentials (SIS-492).
   * False means the signing detour failed and the silent fallback stored
   * the set unsigned — this is how that rate stays observable in PostHog
   * rather than only in Sentry.
   */
  c2paSigned?: boolean;
}

/** Fired when a composed set image is saved to the gallery. */
export function trackSetImageCreated(props: SetImageCreatedProperties): void {
  posthog.capture("set_image_created", props);
}

// ---------------------------------------------------------------------------
// Shopvibes integration (SIS-576)
// ---------------------------------------------------------------------------

interface ImagePushedToShopvibesProperties {
  contentId: string;
  /** showcase image type of the pushed gallery image. */
  imageType?: string;
  /** Image type id in the connected Shopvibes account. */
  shopvibesImageTypeId: string;
  success: boolean;
  durationMs: number;
  /** Size of the batch the push belonged to (1 for a single-image push). */
  batchSize: number;
  /** Where the push was started. */
  source: "viewer" | "library" | "studio";
  errorMessage?: string;
  projectId?: string;
  productId?: string;
}

/** Fired once per image push attempt, successful or not. */
export function trackImagePushedToShopvibes(
  props: ImagePushedToShopvibesProperties
): void {
  posthog.capture("image_pushed_to_shopvibes", props);
}

/** Fired when an image link list is downloaded or copied. */
export function trackImageLinkListCreated(props: {
  count: number;
  delivery: "download" | "clipboard";
  format: "png" | "jpeg";
  aiWatermark: boolean;
}): void {
  posthog.capture("image_link_list_created", props);
}

// ---------------------------------------------------------------------------
// Render-error reporting (PostHog error tracking)
// ---------------------------------------------------------------------------

/** The boundaries that report a crash — keep in step with the CLAUDE.md row. */
export type CrashPanel = "studio-panel" | "image-viewer" | "project-route";

interface PanelCrashedProperties {
  /** Which boundary caught the error. */
  panel: CrashPanel;
  /**
   * The React component stack at the throw — the one thing a minified
   * production stack trace cannot give us, so it travels as a property to
   * make the crash triageable in PostHog's error tracking.
   */
  componentStack?: string | null;
}

/**
 * Reports a render error caught by an error boundary to PostHog's error
 * tracking (a `$exception` event, not a named custom event). Sentry is
 * reported separately through `reportError`; PostHog owns the triage of
 * production render loops, so this is where the component stack has to land.
 *
 * Never throws: analytics must not turn a caught render error into a second
 * one.
 */
export function trackPanelCrashed(
  error: unknown,
  props: PanelCrashedProperties
): void {
  try {
    posthog.captureException(error, props);
  } catch {
    // Swallowed on purpose — see above.
  }
}

// ---------------------------------------------------------------------------
// Person properties (set once on identify or context change)
// ---------------------------------------------------------------------------

export function setPersonProperties(props: Record<string, unknown>): void {
  posthog.setPersonPropertiesForFlags(props);
  posthog.people?.set(props);
}
